// Customer Birthday/Wedding Anniversary notification shown in the topbar,
// right before the Festival notification — same collapsed-pill / "View More"
// expand pattern as festival-notification.tsx, but backed by
// getUpcomingCustomerEvents (customerDetail.dateOfBirth/weddingAnniversary)
// instead of the static festival calendar JSON.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import moment from "moment";
import { Cake, ChevronDown, ChevronUp, Heart, X } from "lucide-react";
import { getUpcomingCustomerEventsAction } from "../Store/actions/apparelAction";

const EVENT_LIMIT = 10;

type CustomerEvent = {
    customerName: string;
    eventType: "Birthday" | "Wedding Anniversary";
    date: string;
    originalDate: string;
};

// Collapsed pill only: "Today"/"Tomorrow" else a short formatted date.
function relativeDayLabel(dateStr: string, today: moment.Moment): string {
    const diff = moment(dateStr).startOf("day").diff(today, "days");
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    return moment(dateStr).format("ddd, D MMM");
}

// Expanded list rows always show the full date including year (per spec),
// even though every event the backend returns falls within the current year.
function absoluteDayLabel(dateStr: string): string {
    return moment(dateStr).format("DD-MMM-YYYY");
}

function EventIcon({ eventType }: { eventType: CustomerEvent["eventType"] }) {
    const Icon = eventType === "Birthday" ? Cake : Heart;
    return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-rose-600 dark:text-rose-300">
            <Icon className="h-4 w-4" />
        </span>
    );
}

function CustomerEventSection({
    label,
    events,
    accent,
}: {
    label: string;
    events: CustomerEvent[];
    accent?: boolean;
}) {
    return (
        <div>
            <p
                className={
                    "mb-1 px-2 text-[11px] font-bold uppercase tracking-wide " +
                    (accent ? "text-rose-600 dark:text-rose-300" : "text-muted-foreground")
                }
            >
                {label}
            </p>
            <div className="space-y-0.5">
                {events.map((event, idx) => (
                    <div key={`${event.customerName}-${event.eventType}-${idx}`} className="flex items-start gap-2 rounded-lg p-1.5 transition-colors hover:bg-muted">
                        <EventIcon eventType={event.eventType} />
                        <div className="min-w-0 flex-1 leading-none">
                            <div className="flex items-center justify-between gap-2">
                                <p className="truncate text-sm font-semibold leading-tight">{event.customerName}</p>
                                <span className="shrink-0 text-[10px] font-medium text-muted-foreground">
                                    {absoluteDayLabel(event.date)}
                                </span>
                            </div>
                            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{event.eventType}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function CustomerEventNotification() {
    const dispatch = useDispatch();
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { getUpcomingCustomerEventsData } = apparelReducerState;

    const [dismissed, setDismissed] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [panelPos, setPanelPos] = React.useState<{ top: number; right: number } | null>(null);
    const { pathname } = useLocation();

    // Topbar lives in the persistent app-shell layout route, so this never
    // unmounts on its own between screens — but re-fetching on every route
    // change keeps it self-healing (covers any screen that doesn't dispatch
    // a refresh after saving a customer) and means a DOB/anniversary added
    // on one screen shows up as soon as you land on the next one, without
    // waiting for a full page reload.
    React.useEffect(() => {
        dispatch(getUpcomingCustomerEventsAction({ limit: EVENT_LIMIT }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Portal'd to document.body for the same reason as FestivalNotification —
    // this needs to float above page content regardless of unrelated stacking
    // contexts (e.g. a DataTable's sticky columns).
    React.useLayoutEffect(() => {
        if (!expanded) return;
        const update = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (rect) setPanelPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [expanded]);

    const events: CustomerEvent[] = getUpcomingCustomerEventsData?.getUpcomingCustomerEvents || [];
    const primary = events[0];

    if (dismissed || !primary) return null;

    const today = moment().startOf("day");
    const todayStr = today.format("YYYY-MM-DD");
    const todaysEvents = events.filter((e) => e.date === todayStr);
    const upcomingEvents = events.filter((e) => e.date !== todayStr);
    const canExpand = events.length > 1;

    return (
        <div ref={anchorRef} className="relative mz shrink-0">
            <div className="flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-100/80 py-1 pl-1 pr-1.5 text-rose-900 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-100">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
                    {primary.eventType === "Birthday" ? <Cake className="h-3.5 w-3.5" /> : <Heart className="h-3.5 w-3.5" />}
                </span>
                <span className="text-xs">
                    <span className="font-bold">{relativeDayLabel(primary.date, today)}</span>
                    <span className="mx-1 opacity-60">–</span>
                    <span className="max-w-[140px] truncate font-semibold">
                        {primary.customerName}'s {primary.eventType}
                    </span>
                </span>
                {canExpand && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        aria-expanded={expanded}
                        aria-label={expanded ? "Show Less" : "View More customer events"}
                        className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-200 dark:text-rose-200 dark:hover:bg-rose-900"
                    >
                        <span>{expanded ? "Show Less" : "View More"}</span>
                        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    aria-label="Dismiss customer event notification"
                    className="shrink-0 rounded-full text-rose-500 transition-colors hover:bg-rose-200 hover:text-rose-800 dark:hover:bg-rose-900 dark:hover:text-rose-100"
                >
                    <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
            </div>

            {expanded && panelPos && createPortal(
                <div
                    className="fixed z-[999] w-80 rounded-xl border bg-popover px-3 py-2 text-popover-foreground shadow-xl"
                    style={{ top: panelPos.top, right: panelPos.right }}
                >
                    <div className="max-h-96 space-y-1.5 overflow-y-auto">
                        {todaysEvents.length > 0 && <CustomerEventSection label="Today" events={todaysEvents} accent />}
                        {upcomingEvents.length > 0 && <CustomerEventSection label="Upcoming" events={upcomingEvents} />}
                    </div>
                    <button
                        type="button"
                        onClick={() => setExpanded(false)}
                        className="mt-1.5 w-full rounded-lg border py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                        Show Less
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
}

export default CustomerEventNotification;
